import type { Dispatch } from 'react';
import { isDefinedAndNotEmpty } from '@meterup/common';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { matchPath, useLocation } from 'react-router';

import { titles } from '../generated/titles';
import { useCurrentCompanyFromPathOrNull } from '../hooks/useCurrentCompanyFromPath';

export const TITLE_COMPONENT_SEPARATOR = ' - ';

interface PageTitleContextValue {
  entityTitle: string | undefined;
  setEntityTitle: Dispatch<string | undefined>;
  getPageTitle: () => string;
}

const PageTitleContext = createContext<PageTitleContextValue>({
  entityTitle: undefined,
  setEntityTitle: () => {},
  getPageTitle: () => '',
});

export function usePageTitleContext() {
  return useContext(PageTitleContext);
}

export function useSetEntityTitle(entityTitle: string | undefined) {
  const { setEntityTitle } = usePageTitleContext();
  useEffect(() => {
    setEntityTitle(entityTitle);
  }, [entityTitle, setEntityTitle]);
}

export default function AutoPageTitle({ children }: { children: React.ReactNode }) {
  const company = useCurrentCompanyFromPathOrNull();
  const { pathname } = useLocation();

  const [entityTitle, setEntityTitle] = useState<string | undefined>();

  // Reset the entity title when the pathname changes
  useEffect(() => {
    setEntityTitle(undefined);
  }, [pathname]);

  const getPageTitle = useCallback(() => {
    const titleResult = Object.entries(titles).find(([pattern]) => !!matchPath(pattern, pathname));

    const components = [
      entityTitle,
      titleResult?.[1],
      company?.company_slug,
      'Meter Dashboard',
    ].filter(isDefinedAndNotEmpty);

    return components.join(TITLE_COMPONENT_SEPARATOR);
  }, [company, pathname, entityTitle]);

  useEffect(() => {
    document.title = getPageTitle();
  }, [getPageTitle]);

  const contextValue = useMemo(
    () => ({
      entityTitle,
      setEntityTitle,
      getPageTitle,
    }),
    [entityTitle, setEntityTitle, getPageTitle],
  );

  return <PageTitleContext.Provider value={contextValue}>{children}</PageTitleContext.Provider>;
}
